import type { RelatedTransferResponse } from '@on3/api';
import type {
  ITransferPortalStatusTypes,
  TransferPortalPageProps,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { useSite } from '@on3/ui-lib/index';
import { formatFilterOptions } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { useRouter } from 'next/router';

import { FilterTeams } from '../Filters/FilterTeams';

const transferPortalStatusTypes: ITransferPortalStatusTypes[] = [
  { value: '', label: 'Status' },
  { value: 'best-available', label: 'Best-Available' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'committed', label: 'Committed' },
  { value: 'signed', label: 'Signed' },
  { value: 'enrolled', label: 'Enrolled' },
];

interface ITransferPortalFiltersProps {
  relatedModel?: RelatedTransferResponse;
  filters?: TransferPortalPageProps['filters'];
  hideFilters?: boolean;
  className?: string;
}
export const TransferPortalFilters = ({
  relatedModel,
  filters,
  hideFilters,
  className,
}: ITransferPortalFiltersProps) => {
  const { formatHref } = useDynamicRoute();
  const { defaultYears } = useSite();
  const router = useRouter();
  const { query } = router;

  const { year, sport, status, type, position } = relatedModel || {};
  const { years, teams, sports, positions } = filters || {};

  const filteredYears = years?.filter(
    (yr) => yr >= 2021 && yr <= defaultYears.transferYear,
  );
  const isTop = type === 'best-available';

  const filterStatus = isTop
    ? transferPortalStatusTypes.filter(
        (statusType) =>
          statusType?.value !== 'withdrawn' &&
          statusType?.value !== 'signed' &&
          statusType?.value !== 'enrolled',
      )
    : transferPortalStatusTypes.filter(
        (statusType) => statusType?.value !== 'best-available',
      );

  const filterYears =
    !!filteredYears &&
    formatFilterOptions({
      data: filteredYears.map((y) => {
        return { value: y.toString() };
      }),
      labelKey: 'value',
      valueKey: 'value',
    });
  const filterSports =
    !!sports &&
    formatFilterOptions({
      data: sports,
      labelKey: 'name',
      valueKey: 'name',
    });
  const filterPositions =
    !!positions &&
    formatFilterOptions({
      data: positions,
      labelKey: 'abbreviation',
      valueKey: 'abbreviation',
      defaultOption: 'Positions',
    });

  return (
    <div className={className}>
      {teams && (
        <FilterTeams
          collegePathName={`/college/[college]/transfer-portal/[transferType]/[sport]/[[...year]]/`}
          name="college"
          teams={teams}
        />
      )}
      {!!filterYears && (
        <Filter
          name="year"
          navigate={(_, v) =>
            formatHref({
              params: { year: v },
            })
          }
          options={filterYears}
          value={String(year || query.year || defaultYears.transferYear)}
        />
      )}
      {!!filterSports && (
        <Filter
          name="sport"
          navigate={(_, v) =>
            formatHref({ params: { sport: v }, query: { position: undefined } })
          }
          options={filterSports}
          value={sport?.name ? slugify(sport.name) : 'football'}
        />
      )}
      {!!filterStatus && (
        <Filter
          name="status"
          navigate={(_, v) =>
            formatHref({
              query: {
                status: v === '' ? undefined : v,
              },
            })
          }
          options={filterStatus}
          value={
            status
              ? status === 'Entered'
                ? 'best-available'
                : slugify(status)
              : null
          }
        />
      )}
      {!!filterPositions && (
        <Filter
          name="position"
          navigate={(_, v) =>
            formatHref({
              query: {
                position: v === 'Positions' ? undefined : v,
              },
            })
          }
          options={filterPositions}
          value={position?.abbr ? slugify(position.abbr) : null}
        />
      )}
    </div>
  );
};
